/* =============================================================================
   VIEWS / ADMIN / NUTRITION-CACHE — synchrone Komposition für Hot-Paths (S152 A2)
   =============================================================================
   KDS-Kacheln und der Order-Detail-Dialog brauchen pro Position komponierte
   Nährwerte (Basis + Optionen − abgewählte). Ein RPC-Roundtrip je Position wäre
   im Realtime-KDS-Grid zu teuer und würde das non-destruktive Render-Pattern
   brechen. Deshalb laden wir Zutaten + benannte Einheiten + ALLE Rezepte (Basis
   und Options-menu_items) EINMAL in einen Modul-Cache und rechnen anschließend
   synchron über `composeNutrition` (core/nutrition.js) — identische Formel wie
   die SQL-RPC `compose_nutrition`, also deckungsgleich zum Badge.

   Hinter Feature-Flag `ingredientsDb`. Alles defensiv: nie werfen, bei nicht
   geladenem Cache / fehlenden Daten `null` zurückgeben (Aufrufer zeigt dann
   einfach nichts). `invalidate()` nach Rezept-Änderung erzwingt Neuladen.
============================================================================ */

import { composeNutrition, composeLines, defaultOptionsFor, computeNutrition } from '../../core/nutrition.js';
import { buildInstructionSteps } from '../../core/build-instructions.js';
import { aggregateCost } from '../../core/food-cost.js';

const _DRAFT_ID = '__draft__';

let _loaded = false;
let _loading = null;
let _ingredientsById = {};
let _unitsByIng = {};       // ingredientId → [{unitName, gramsOrMl}]
let _recipesByItem = {};    // menuItemId → [recipeItems]

function flagOn() {
  return !!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb);
}
function storage() { return window.App && window.App.storage; }
function menuState() {
  const st = storage() && storage()._getInternalState && storage()._getInternalState();
  return (st && st.menu) || [];
}

/** Lädt den Cache (idempotent, in-flight-aware). Liefert true, wenn nutzbar. */
export async function ensureLoaded() {
  if (!flagOn() || !storage()) return false;
  if (_loaded) return true;
  if (_loading) return _loading;
  _loading = (async () => {
    try {
      const menu = menuState();
      const menuIds = menu.map(m => m.id).filter(Boolean);
      const ingredients = await storage().ingredients.list();
      const ingIds = ingredients.map(i => i.id);
      const [units, recipes] = await Promise.all([
        storage().ingredientUnits.listForMany(ingIds),
        storage().recipes.listForMany(menuIds)
      ]);
      _ingredientsById = Object.fromEntries(ingredients.map(i => [i.id, i]));
      _unitsByIng = {};
      (units || []).forEach(u => {
        (_unitsByIng[u.ingredientId] = _unitsByIng[u.ingredientId] || []).push(u);
      });
      _recipesByItem = {};
      (recipes || []).forEach(r => {
        (_recipesByItem[r.menuItemId] = _recipesByItem[r.menuItemId] || []).push(r);
      });
      _loaded = true;
      return true;
    } catch (e) {
      console.warn('[nutrition-cache] load:', e);
      _loaded = false;
      return false;
    } finally {
      _loading = null;
    }
  })();
  return _loading;
}

/** Cache verwerfen (nach Rezept-Speichern). Nächster ensureLoaded lädt frisch. */
export function invalidate() {
  _loaded = false; _loading = null;
  _ingredientsById = {}; _unitsByIng = {}; _recipesByItem = {};
}

/**
 * Synchrone komponierte Nährwerte einer Bestellposition. `null`, wenn der Cache
 * nicht geladen ist oder das Gericht kein Rezept hat (ingredientCount=0).
 * @param {string} menuItemId
 * @param {object} customizations
 * @returns {object|null} wie composeNutrition, oder null
 */
export function composeLineSync(menuItemId, customizations) {
  if (!_loaded || !menuItemId) return null;
  const menu = menuState();
  const mi = menu.find(m => m.id === menuItemId);
  const defaults = mi ? defaultOptionsFor(mi, menu) : { bun: null, patty: null };
  const n = composeNutrition(menuItemId, customizations || {}, {
    recipesByItem: _recipesByItem,
    ingredientsById: _ingredientsById,
    namedUnitsByIngredient: _unitsByIng,
    defaults
  });
  return (n && n.ingredientCount > 0) ? n : null;
}

/**
 * Synchrone Bauanleitung (S154) einer Bestellposition aus demselben Cache.
 * `null`, wenn Cache nicht geladen oder das Gericht kein Rezept hat.
 * @param {string} menuItemId
 * @param {object} customizations
 * @returns {Array|null} Schritte aus buildInstructionSteps, oder null
 */
export function buildStepsSync(menuItemId, customizations) {
  if (!_loaded || !menuItemId) return null;
  const menu = menuState();
  const mi = menu.find(m => m.id === menuItemId);
  const defaults = mi ? defaultOptionsFor(mi, menu) : { bun: null, patty: null };
  const steps = buildInstructionSteps(menuItemId, customizations || {}, {
    recipesByItem: _recipesByItem,
    ingredientsById: _ingredientsById,
    defaults,
    isBurger: !!(mi && mi.meta && mi.meta.isBurger)  // S154b — Bun-Bookending erzwingen
  });
  return (steps && steps.length) ? steps : null;
}

/**
 * S155 — Live-Nährwerte eines NOCH NICHT gespeicherten Gerichts (Konfigurator):
 * Default-komponiert (Basis = draftRows + Default-Bun/-Patty aus draftMeta). Nutzt
 * die Cache-Maps für Options-Rezepte + Zutaten + Einheiten. `null` wenn Cache leer
 * oder draftRows leer.
 * @param {Array} draftRows - Basis-Rezeptzeilen des Drafts (ingredientId, quantity, unit, isOptional)
 * @param {object} draftMenuItem - { meta } für defaultOptionsFor
 * @returns {object|null}
 */
export function composeDraftSync(draftRows, draftMenuItem) {
  if (!_loaded) return null;
  const rows = Array.isArray(draftRows) ? draftRows.filter(r => r && r.ingredientId) : [];
  if (!rows.length) return computeNutrition([], _ingredientsById, _unitsByIng); // 0-Werte statt null bei leerem Rezept
  const menu = menuState();
  const defaults = draftMenuItem ? defaultOptionsFor(draftMenuItem, menu) : { bun: null, patty: null };
  const recipesByItem = Object.assign({}, _recipesByItem, { [_DRAFT_ID]: rows });
  return composeNutrition(_DRAFT_ID, {}, {
    recipesByItem,
    ingredientsById: _ingredientsById,
    namedUnitsByIngredient: _unitsByIng,
    defaults
  });
}

/** S156 — Roh-Katalog (Zutaten + alle Rezepte) für den generativen Konfigurator. */
export function getCatalog() {
  return { ingredientsById: _ingredientsById, recipesByItem: _recipesByItem, unitsByIng: _unitsByIng };
}

// =============================================================================
// S158 — Food-Cost / Wareneinsatz (synchron, gleicher Cache, gleiche Item-Menge
// wie die Nährwerte → composeLines ist die Single-Source der Item-Auswahl).
// =============================================================================

/**
 * Wareneinsatz einer Bestellposition / eines Default-Gerichts. Spiegel von
 * `compose_cost`. Nutzt EXAKT dieselben Items wie composeNutrition (composeLines),
 * damit Kosten und Nährwerte dasselbe Gericht abbilden.
 * @returns {{goodsCost,missingData,ingredientCount}|null}
 */
export function composeCostSync(menuItemId, customizations) {
  if (!_loaded || !menuItemId) return null;
  const menu = menuState();
  const mi = menu.find(m => m.id === menuItemId);
  const defaults = mi ? defaultOptionsFor(mi, menu) : { bun: null, patty: null };
  const entries = composeLines(menuItemId, customizations || {}, {
    recipesByItem: _recipesByItem, ingredientsById: _ingredientsById, defaults
  });
  const forCost = entries.filter(e => e.kind !== 'remove' && e.ingredientId);
  return aggregateCost(forCost, _ingredientsById, _unitsByIng);
}

/**
 * S158 — Live-Wareneinsatz eines NOCH NICHT gespeicherten Gerichts (Konfigurator),
 * analog composeDraftSync: Basis = draftRows + Default-Bun/-Patty aus draftMeta.
 * `null` wenn Cache nicht geladen.
 * @returns {{goodsCost,missingData,ingredientCount}|null}
 */
export function composeCostDraftSync(draftRows, draftMenuItem) {
  if (!_loaded) return null;
  const rows = Array.isArray(draftRows) ? draftRows.filter(r => r && r.ingredientId) : [];
  const menu = menuState();
  const defaults = draftMenuItem ? defaultOptionsFor(draftMenuItem, menu) : { bun: null, patty: null };
  const recipesByItem = Object.assign({}, _recipesByItem, { [_DRAFT_ID]: rows });
  const entries = composeLines(_DRAFT_ID, {}, {
    recipesByItem, ingredientsById: _ingredientsById, defaults
  });
  const forCost = entries.filter(e => e.kind !== 'remove' && e.ingredientId);
  return aggregateCost(forCost, _ingredientsById, _unitsByIng);
}

if (typeof window !== 'undefined') {
  window.App = window.App || {};
  window.App.nutritionCache = {
    ensureLoaded, invalidate, composeLineSync, buildStepsSync, composeDraftSync, getCatalog,
    composeCostSync, composeCostDraftSync
  };
}
